import { useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Landmark, Loader2, Send, CheckCircle2 } from "lucide-react";
import { API } from "@/lib/api";
import { UEMOA_BANKS, COUNTRY_LABELS, monthlyPayment } from "@/lib/banks";

const DURATIONS = [6, 12, 24, 36, 48, 60];
const fmt = (n) => Math.round(n).toLocaleString("fr-FR");

export const CreditSimulator = ({ articleRef = "", defaultAmount = 500000, defaultCountry = "CI" }) => {
  const [country, setCountry] = useState(COUNTRY_LABELS[defaultCountry] ? defaultCountry : "CI");
  const [amount, setAmount] = useState(Math.max(200000, Math.round(defaultAmount)));
  const [months, setMonths] = useState(24);
  const [selectedBank, setSelectedBank] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const banks = UEMOA_BANKS[country] || [];

  const rows = useMemo(
    () => banks.map((b) => ({ ...b, monthly: monthlyPayment(amount, b.rate, months) })),
    [banks, amount, months]
  );

  const bank = selectedBank || (rows.length ? rows.reduce((a, b) => (a.monthly <= b.monthly ? a : b)) : null);
  const total = bank ? bank.monthly * months : 0;
  const interest = total - amount;

  const submit = async () => {
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Nom et téléphone obligatoires");
      return;
    }
    if (!bank) return;
    setBusy(true);
    try {
      await axios.post(`${API}/financing-leads`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        amount,
        duration_years: Math.round((months / 12) * 100) / 100,
        monthly: Math.round(bank.monthly),
        country,
        bank: bank.name,
        rate: bank.rate,
        article_ref: articleRef,
        note: `Simulation crédit ${fmt(amount)} FCFA sur ${months} mois`,
      });
      setSent(true);
      toast.success("Demande envoyée ! Nous vous mettons en relation avec la banque.");
    } catch {
      toast.error("Erreur d'envoi, réessayez.");
    }
    setBusy(false);
  };

  if (sent) {
    return (
      <div className="text-center py-6" data-testid="credit-sim-success">
        <CheckCircle2 size={48} className="text-emerald-500 mx-auto mb-3" />
        <div className="font-bold text-lg">Demande enregistrée !</div>
        <p className="text-sm text-[var(--ink-soft)] mt-2 max-w-sm mx-auto">
          Notre équipe transmet votre dossier à <b>{bank?.name}</b> ({COUNTRY_LABELS[country]}) et vous
          recontacte sous 48h. Les pièces justificatives (CNI, justificatif de revenus) vous seront
          demandées directement par la banque.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="credit-simulator">
      {/* Pays */}
      <label className="block text-xs font-bold text-[var(--ink-soft)] mb-1">Pays</label>
      <select
        value={country}
        onChange={(e) => { setCountry(e.target.value); setSelectedBank(null); }}
        className="w-full text-sm rounded-xl border border-[var(--line)] p-2.5 mb-3 bg-white"
        data-testid="credit-sim-country"
      >
        {Object.keys(UEMOA_BANKS).map((c) => (
          <option key={c} value={c}>{COUNTRY_LABELS[c]}</option>
        ))}
      </select>

      {/* Montant */}
      <label className="block text-xs font-bold text-[var(--ink-soft)] mb-1">
        Montant : <span className="text-orange-600">{fmt(amount)} FCFA</span>
      </label>
      <input
        type="range" min={200000} max={10000000} step={50000} value={amount}
        onChange={(e) => setAmount(Number(e.target.value))}
        className="w-full mb-3 accent-orange-500"
        data-testid="credit-sim-amount"
      />

      {/* Durée */}
      <label className="block text-xs font-bold text-[var(--ink-soft)] mb-1">Durée</label>
      <div className="flex flex-wrap gap-2 mb-4">
        {DURATIONS.map((m) => (
          <button
            key={m} type="button" onClick={() => setMonths(m)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
              months === m ? "bg-orange-500 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
            data-testid={`credit-sim-duration-${m}`}
          >
            {m} mois
          </button>
        ))}
      </div>

      {/* Choix de la banque (menu deroulant) + comparateur */}
      <div className="mb-3">
        <label className="text-xs font-bold uppercase tracking-widest text-[var(--ink-soft)]">Votre banque</label>
        <select
          value={bank?.name || ""}
          onChange={(e) => setSelectedBank(rows.find((r) => r.name === e.target.value) || null)}
          data-testid="credit-sim-bank-select"
        >
          {rows.map((b) => (
            <option key={b.name} value={b.name}>{b.name} — {(b.rate * 100).toFixed(1)} %{b.fees ? ` + ${(b.fees * 100).toFixed(0)} % frais` : ""}</option>
          ))}
        </select>
        {bank?.note && <div className="text-[11px] text-[var(--ink-soft)] mt-1" data-testid="credit-sim-bank-note">{bank.note} · Taux indicatif (plafond d&apos;usure BCEAO 15 %) — a confirmer avec la banque.</div>}
      </div>
      <details className="mb-4">
        <summary className="text-xs font-semibold text-orange-600 cursor-pointer" data-testid="credit-sim-compare-toggle">Comparer toutes les banques ({rows.length})</summary>
      <div className="space-y-2 mt-2" data-testid="credit-sim-banks">
        {rows.map((b) => {
          const active = bank?.name === b.name;
          return (
            <button
              key={b.name} type="button" onClick={() => setSelectedBank(b)}
              className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl border text-left transition-colors ${
                active ? "border-orange-400 bg-orange-50" : "border-[var(--line)] bg-white hover:bg-gray-50"
              }`}
              data-testid={`credit-sim-bank-${b.name.replace(/[^a-zA-Z0-9]+/g, "-")}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <Landmark size={16} className={active ? "text-orange-600" : "text-gray-400"} />
                <div className="min-w-0">
                  <div className="font-bold text-sm truncate">{b.name}</div>
                  <div className="text-[11px] text-[var(--ink-soft)]">Taux indicatif {(b.rate * 100).toFixed(1)}%</div>
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-extrabold text-sm text-orange-600">{fmt(b.monthly)}</div>
                <div className="text-[10px] text-[var(--ink-soft)]">FCFA/mois</div>
              </div>
            </button>
          );
        })}
      </div>
      </details>

      {/* Résumé */}
      {bank && (
        <div className="grid grid-cols-3 gap-2 mb-4 text-center">
          <Metric label="Mensualité" value={`${fmt(bank.monthly)}`} />
          <Metric label="Coût total" value={`${fmt(total)}`} />
          <Metric label="Intérêts" value={`${fmt(interest)}`} />
        </div>
      )}

      {/* Formulaire lead */}
      <div className="space-y-2">
        <input
          placeholder="Nom complet *" value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="w-full text-sm rounded-xl border border-[var(--line)] p-2.5"
          data-testid="credit-sim-name"
        />
        <input
          placeholder="Téléphone *" value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
          className="w-full text-sm rounded-xl border border-[var(--line)] p-2.5"
          data-testid="credit-sim-phone"
        />
        <input
          placeholder="Email (optionnel)" value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          className="w-full text-sm rounded-xl border border-[var(--line)] p-2.5"
          data-testid="credit-sim-email"
        />
      </div>

      <button
        type="button" onClick={submit} disabled={busy}
        className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-bold bg-orange-500 text-white hover:bg-orange-600 transition-colors disabled:opacity-50"
        data-testid="credit-sim-submit"
      >
        {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Demander ce crédit
      </button>
      <p className="text-[10px] text-[var(--ink-soft)] mt-2 leading-relaxed">
        Taux et mensualités <b>indicatifs</b>. L'offre finale, l'étude du dossier et les pièces justificatives
        relèvent de la banque. Vos coordonnées sont partagées uniquement avec la banque choisie via notre équipe.
      </p>
    </div>
  );
};

function Metric({ label, value }) {
  return (
    <div className="rounded-xl bg-gray-50 border border-[var(--line)] py-2">
      <div className="font-extrabold text-sm text-[var(--ink)]">{value}</div>
      <div className="text-[10px] text-[var(--ink-soft)]">{label}</div>
    </div>
  );
}
